/**
 * errorMessages.ts
 *
 * Maps backend errors onto user-facing Vietnamese messages.
 *
 * This module is pure: it has no imports beyond the Connect error codes and
 * no side effects, so it can be unit-tested directly.
 */
import { Code } from "@connectrpc/connect";

/** Message shown when no more specific mapping applies. */
export const FALLBACK_ERROR_MESSAGE = "Có lỗi xảy ra. Vui lòng thử lại";

/** Maps Connect error codes to user-friendly Vietnamese messages. */
export const errorCodeToVietnameseMessage: Record<Code, string> = {
  [Code.Canceled]: "Yêu cầu đã bị hủy",
  [Code.Unknown]: FALLBACK_ERROR_MESSAGE,
  [Code.InvalidArgument]: "Dữ liệu không hợp lệ",
  [Code.DeadlineExceeded]: "Yêu cầu quá thời gian chờ. Vui lòng thử lại",
  [Code.NotFound]: "Không tìm thấy dữ liệu",
  [Code.AlreadyExists]: "Dữ liệu đã tồn tại",
  [Code.PermissionDenied]: "Bạn không có quyền thực hiện thao tác này",
  [Code.ResourceExhausted]: "Tài nguyên đã cạn kiệt. Vui lòng thử lại sau",
  [Code.FailedPrecondition]: "Không thể thực hiện thao tác này lúc này",
  [Code.Aborted]: "Thao tác đã bị hủy bỏ. Vui lòng thử lại",
  [Code.OutOfRange]: "Giá trị vượt quá phạm vi cho phép",
  [Code.Unimplemented]: "Tính năng chưa được hỗ trợ",
  [Code.Internal]: "Lỗi hệ thống. Vui lòng thử lại sau",
  [Code.Unavailable]: "Dịch vụ tạm thời không khả dụng. Vui lòng thử lại",
  [Code.DataLoss]: "Dữ liệu bị mất. Vui lòng liên hệ hỗ trợ",
  [Code.Unauthenticated]: "Phiên đăng nhập đã hết hạn",
};

/** Network error patterns, shared between technical errors and retry logic. */
export const networkErrorPatterns = [
  /failed to fetch/i,
  /network request failed/i,
  /ECONNREFUSED/i,
  /ETIMEDOUT/i,
  /ENOTFOUND/i,
  /ECONNRESET/i,
  /timeout/i,
  /localhost/i, // Localhost connection errors (dev environment)
  /net::ERR_/i, // Chrome network errors
];

/** Technical error patterns that must never be shown to users. */
export const technicalErrorPatterns = [
  /unmarshal/i,
  /protobuf/i,
  /wire format/i,
  ...networkErrorPatterns, // Include network patterns
  /database/i,
  /SQL/i,
  /connection/i,
];

/**
 * Converts a raw backend error message into one safe to show a user.
 *
 * Applies three rules in order:
 *  1. A message matching a technical pattern is discarded and replaced by the
 *     code's mapped message, so internal details never leak.
 *  2. A message already containing Vietnamese characters is passed through,
 *     since the backend authored it for the user.
 *  3. Anything else is replaced by the code's mapped message.
 *
 * @param message - The raw message from the backend.
 * @param code - The Connect error code accompanying the message.
 * @returns A Vietnamese message safe to display.
 */
export function sanitizeErrorMessage(message: string, code: Code): string {
  // Check if message contains technical patterns
  const isTechnicalError = technicalErrorPatterns.some((pattern) =>
    pattern.test(message)
  );

  if (isTechnicalError) {
    // Return generic message based on error code
    return errorCodeToVietnameseMessage[code] || FALLBACK_ERROR_MESSAGE;
  }

  // If message is already in Vietnamese (contains Vietnamese characters), use it
  const hasVietnameseChars = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i.test(
    message
  );
  if (hasVietnameseChars) {
    return message;
  }

  // For non-Vietnamese, non-technical messages, use code mapping
  return errorCodeToVietnameseMessage[code] || FALLBACK_ERROR_MESSAGE;
}
